import { useEffect, useState } from "react";
import { getTrainingSessionDiagnostics, type TrainingSessionDiagnosticsView } from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";

/**
 * 실패 원인 진단을 한 번 불러오는 로더. 분석 및 평가 탭이 실패 상태 세션에서만 켠다.
 *
 * 진단은 terminal 상태에서 바뀌지 않으므로 폴링하지 않는다. `enabled` 가 다시 켜질 때(탭 재방문)
 * 마다 다시 받지만, 성공 데이터가 있으면 로딩 상태로 되돌리지 않는다.
 */
export function useSessionDiagnostics(
  sessionId: string,
  options: { enabled: boolean },
): { state: LoadState<TrainingSessionDiagnosticsView>; refetch: () => void } {
  const { enabled } = options;
  const [state, setState] = useState<LoadState<TrainingSessionDiagnosticsView>>({
    status: "idle",
  });
  const [refetchTick, setRefetchTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setState((prev) => (prev.status === "success" ? prev : { status: "loading" }));
    getTrainingSessionDiagnostics(sessionId).then(
      (data) => {
        if (!cancelled) {
          setState({ status: "success", data });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId, enabled, refetchTick]);

  return { state, refetch: () => setRefetchTick((tick) => tick + 1) };
}
